import { useState } from 'react';

import { match } from 'ts-pattern';

import toast from 'lib/common/utils/toast';

import { AuthStages, AuthState, MfaDestination, MfaDestinations } from '../types/AuthState';
import { useHandleAuthStep } from './useHandleAuthStep';

export const useMfaFlow = ({
  setInvalidCode,
  setAuthState,
  setError,
  authState,
}: {
  setInvalidCode: (invalid: boolean) => void;
  setError: (error: boolean) => void;
  authState: AuthState;
  setAuthState: (state: AuthState) => void;
}) => {
  const [mfaDestinations, setMfaDestinations] = useState<{
    obfuscatedEmail: string | undefined;
    obfuscatedPhone: string | undefined;
  } | null>(null);

  const { handleAuthStep } = useHandleAuthStep({ setMfaDestinations, setInvalidCode, setError });

  const selectMfaDestination = async (mfaSelection: MfaDestination, existingState?: AuthState) => {
    const stateToUse = existingState || authState;
    const newState = await handleAuthStep(
      match({ mfaSelection, authState: stateToUse })
        .with(
          { authState: { stage: AuthStages.mfa_select }, mfaSelection: MfaDestinations.email },
          ({ mfaSelection, authState: { obfuscatedEmail, authResponse } }) => ({
            authResponse,
            stage: AuthStages.mfa_selected,
            mfaSelection,
            obfuscatedEmail,
          })
        )
        .with(
          { authState: { stage: AuthStages.mfa_select }, mfaSelection: MfaDestinations.sms },
          ({ mfaSelection, authState: { obfuscatedPhone, authResponse } }) => ({
            authResponse,
            stage: AuthStages.mfa_selected,
            mfaSelection,
            obfuscatedPhone,
          })
        )
        .otherwise(() => {
          throw `Invalid auth state: ${authState}`;
        })
    );
    setAuthState(newState);
  };

  const provideMfaCode = async (mfaCode: string) => {
    if (!('authResponse' in authState) || !('mfaSelection' in authState)) {
      throw `Invalid auth state: ${authState}`;
    }

    setInvalidCode(false);
    try {
      const newState = await handleAuthStep({ ...authState, stage: AuthStages.mfa_entered, mfaCode });
      setAuthState(newState);

      if (newState.stage === 'mfa_entry') {
        setInvalidCode(true);
        throw 'Invalid code';
      }
    } catch (error: any) {
      if (
        error.toString().includes('VerificationCheck was not found') || // Twilio timeout
        error.toString().includes('Invalid session for the user') // Cognito timeout
      ) {
        toast('info', "Oops, it looks like your login session has expired. We'll get you to try and verify again.");
        const newState = await handleAuthStep({ stage: AuthStages.initial });
        setAuthState(newState);
      }

      if (
        error.toString().includes('Max check attempts reached') || // Twilio max attempts
        error.toString().includes('Incorrect username or password') // Cognito max attempts
      ) {
        toast('info', "Oops, you've tried a wrong code too many times. Give it a few minutes and try again.");
        setInvalidCode(true);
        const newState = await handleAuthStep({ stage: AuthStages.initial });
        setAuthState(newState);
      }

      throw error;
    }
  };

  return {
    mfaDestinations,
    setMfaDestinations,
    selectMfaDestination,
    provideMfaCode,
    mfaSelection: 'mfaSelection' in authState ? authState.mfaSelection : undefined,
  };
};
